/**
 * IndexedDB schema and connection for the offline-checks feature.
 *
 * Three object stores, mirroring design §5.2:
 *   - offline-sessions   keyed by session id, indexed by status
 *   - offline-layer-cache keyed by layer id, indexed by label
 *   - etag-cache          keyed by full request URL
 *
 * Gated behind `enable_offline_checks` in `root_config`. Callers MUST
 * obtain the DB via `getOfflineDB()` (not `openOfflineDB()` directly), and
 * MUST first verify the flag via `isOfflineChecksEnabled(config)` from the
 * caller's React context. Opening the DB is intentionally lazy: the first
 * call materialises it; subsequent calls return the cached promise.
 *
 * Design reasoning (see also docs/designs/EFQ-2962/...):
 *   - Per the lessons from PR #1585 (§5.1), this DB only stores the things
 *     the offline feature explicitly opts into: session state, the
 *     pre-downloaded pallet directory, and ETag-keyed API responses for
 *     the four polling endpoints. It is NOT a generic API cache.
 *   - The DB is not namespaced per tenant. Tenant-switch invalidation is
 *     handled separately by `invalidateOnTenantSwitch()` (issue 3.3).
 */

import type { DBSchema, IDBPDatabase } from "idb";
import { openDB } from "idb";

import { isOfflineChecksAllowedForTenant } from "./allowedTenants";
import type { CachedLayer, ETagCacheEntry, OfflineCheckSession } from "./types";

export const OFFLINE_DB_NAME = "avos-offline";
export const OFFLINE_DB_VERSION = 1;

export const STORE_SESSIONS = "offline-sessions";
export const STORE_LAYER_CACHE = "offline-layer-cache";
export const STORE_ETAG_CACHE = "etag-cache";

export interface OfflineDB extends DBSchema {
    [STORE_SESSIONS]: {
        key: string;
        value: OfflineCheckSession;
        indexes: { "by-status": string };
    };
    [STORE_LAYER_CACHE]: {
        key: string;
        value: CachedLayer;
        indexes: { "by-label": string };
    };
    [STORE_ETAG_CACHE]: {
        key: string;
        value: ETagCacheEntry;
    };
}

let dbPromise: Promise<IDBPDatabase<OfflineDB>> | null = null;

function openOfflineDB(): Promise<IDBPDatabase<OfflineDB>> {
    return openDB<OfflineDB>(OFFLINE_DB_NAME, OFFLINE_DB_VERSION, {
        upgrade(db) {
            if (!db.objectStoreNames.contains(STORE_SESSIONS)) {
                const sessions = db.createObjectStore(STORE_SESSIONS, { keyPath: "id" });
                sessions.createIndex("by-status", "status");
            }
            if (!db.objectStoreNames.contains(STORE_LAYER_CACHE)) {
                const layers = db.createObjectStore(STORE_LAYER_CACHE, { keyPath: "id" });
                layers.createIndex("by-label", "label");
            }
            if (!db.objectStoreNames.contains(STORE_ETAG_CACHE)) {
                db.createObjectStore(STORE_ETAG_CACHE, { keyPath: "url" });
            }
        },
    });
}

/**
 * Returns a memoised promise to the offline IndexedDB. The first call
 * materialises the DB and runs any pending migrations; subsequent calls
 * return the same promise. Safe to call from any async caller.
 *
 * Important: this does NOT itself check the feature flag — callers must
 * verify via `isOfflineChecksEnabled()` first. We deliberately keep this
 * function flag-agnostic so unit tests can drive it without setting up
 * React context.
 */
export function getOfflineDB(): Promise<IDBPDatabase<OfflineDB>> {
    if (!dbPromise) {
        dbPromise = openOfflineDB();
    }
    return dbPromise;
}

/**
 * For tests and tenant-switch invalidation: drop the cached connection
 * promise so the next `getOfflineDB()` call reopens the DB. Does not
 * clear the DB contents — see `clearOfflineDB()` for that.
 */
export function resetOfflineDBConnection(): void {
    dbPromise = null;
}

/**
 * Wipe every object store. Used by the tenant-switch invalidation hook
 * (issue 3.3) before the new tenant's data is loaded.
 */
export async function clearOfflineDB(): Promise<void> {
    const db = await getOfflineDB();
    const tx = db.transaction(
        [STORE_SESSIONS, STORE_LAYER_CACHE, STORE_ETAG_CACHE],
        "readwrite",
    );
    await Promise.all([
        tx.objectStore(STORE_SESSIONS).clear(),
        tx.objectStore(STORE_LAYER_CACHE).clear(),
        tx.objectStore(STORE_ETAG_CACHE).clear(),
    ]);
    await tx.done;
}

/**
 * Source-of-truth check for the master feature flag. Pass the result of
 * `useConfig()` (or any object with the same shape, in tests). Returns
 * strict-equal-true so missing keys, undefined, and `false` all evaluate
 * to false — no tenant ever silently opts in.
 *
 * Two independent conditions must hold:
 *   1. `root_config.enable_offline_checks === true` — the per-tenant
 *      opt-in toggle.
 *   2. The tenant_id is on the hard-coded allowlist in `./allowedTenants`.
 *      Acts as a kill-switch: even if (1) is somehow true on a non-allowed
 *      tenant (manual DB edit, stale config), the feature stays off.
 *
 * The argument is `unknown` because `useConfig()` returns `RootConfigType`,
 * which the codebase has not fully typed yet (`root_config` is one of the
 * fields not declared on the type — see the TODO in `configHook.tsx`).
 * Narrowing happens at runtime with null-safe access.
 */
export function isOfflineChecksEnabled(config: unknown): boolean {
    if (typeof config !== "object" || config === null) return false;
    const rootConfig = (config as { root_config?: unknown }).root_config;
    if (typeof rootConfig !== "object" || rootConfig === null) return false;
    if ((rootConfig as { enable_offline_checks?: unknown }).enable_offline_checks !== true) {
        return false;
    }
    const tenant_id = (config as { tenant_id?: unknown }).tenant_id;
    return isOfflineChecksAllowedForTenant(tenant_id);
}
